// src/components/AssetClassChip.jsx
//
// Visual indicator + helper hook for the bot config form.
//
// Exports:
//   - <AssetClassChip assetClass={...} /> — small badge for header rows
//   - useAssetClass(exchange) — hook that derives asset_class from selected exchange
//   - getAssetClassFromExchange(exchange) — pure function for same logic
//
// Asset class values:
//   'crypto'        — Binance, Coinbase, sim crypto
//   'stock_equity'  — Alpaca (paper or live)
//
// This drives every conditional in the form: symbol picker shape,
// sizing units, universe selector, extended hours toggle, etc.

import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';
import { CurrencyBitcoin, ShowChart } from '@mui/icons-material';


export function getAssetClassFromExchange(exchange) {
  if (!exchange) return 'crypto';
  const name = String(exchange.exchange || exchange.type || '').toLowerCase();
  if (name === 'alpaca' || name.startsWith('alpaca.')) return 'stock_equity';
  return 'crypto';
}


export function useAssetClass(selectedExchange) {
  return React.useMemo(
    () => getAssetClassFromExchange(selectedExchange),
    [selectedExchange]
  );
}


export function AssetClassChip({ assetClass, size = 'small' }) {
  if (!assetClass) return null;

  const isStock = assetClass === 'stock_equity';
  const config = isStock
    ? {
        label: 'US STOCKS',
        icon: <ShowChart sx={{ fontSize: 14 }} />,
        bg: '#1e2a4a',
        fg: '#9cc3e4',
        border: '#4d8fc7',
        tooltip: 'US equities — 9:30am–4:00pm ET. Fractional shares supported. Subject to PDT rules.',
      }
    : {
        label: 'CRYPTO',
        icon: <CurrencyBitcoin sx={{ fontSize: 14 }} />,
        bg: '#451a03',
        fg: '#d9a441',
        border: '#92400e',
        tooltip: 'Cryptocurrency — 24/7 trading. No PDT restrictions. No market hours.',
      };

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600}>{config.label}</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {config.tooltip}
          </Typography>
        </Box>
      }
      arrow
    >
      <Chip
        size={size}
        icon={config.icon}
        label={config.label}
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: config.bg,
          color: config.fg,
          border: `1px solid ${config.border}`,
          '& .MuiChip-icon': { color: config.fg },
        }}
      />
    </Tooltip>
  );
}